import { Box, Column, Dialog, Button, Row, Text } from "@hightouch/htui";

import type * as SyncErrors from "src/types/sync-errors";
import type { SyncRunStatus } from "src/utils/syncs";

import {
  ErrorOriginInfoModal,
  generateLinkProps,
  type Sync,
} from "src/pages/syncs/sync/components/error-origin-info-modal";
import {
  getErrorCode,
  getSpecialCaseErrorInfoContent,
  isSpecialCaseErrorCode,
} from "src/pages/syncs/sync/components/error-utils";
import { Link } from "src/router";
import { Markdown } from "src/ui/markdown";

type SyncRequestErrorModalProps = {
  isOpen: boolean;
  sync?: Sync;
  syncRequestError?: SyncErrors.SyncRequestErrorInfo;
  syncStatus?: SyncRunStatus;
  onClose: () => void;
};

export const SyncRequestErrorModal = ({
  isOpen,
  sync,
  syncRequestError,
  syncStatus,
  onClose,
}: SyncRequestErrorModalProps) => {
  if (!syncRequestError) return null;

  const errorCode = getErrorCode(syncRequestError, syncStatus);

  if (syncRequestError.originInfo && !isSpecialCaseErrorCode(errorCode)) {
    return (
      <ErrorOriginInfoModal
        isOpen={isOpen}
        onClose={onClose}
        originInfo={syncRequestError.originInfo}
        sync={sync}
        syncRequestError={syncRequestError}
      />
    );
  }

  const { title, message, links } = isSpecialCaseErrorCode(errorCode)
    ? getSpecialCaseErrorInfoContent(errorCode, syncRequestError)
    : {
        title: "Run error",
        message:
          syncRequestError?.userFacingMessage ||
          syncRequestError?.message ||
          "We apologize for the inconvenience, but an unknown error has occurred. Please contact our customer support team for assistance.",
      };

  return (
    <Dialog
      isOpen={isOpen}
      variant="info"
      width="lg"
      title={title}
      actions={<Button onClick={onClose}>Close</Button>}
      onClose={onClose}
    >
      <Column gap={2} flex={1} minHeight={0}>
        <Box flex={1} minHeight={0} overflowY="auto">
          <Text>
            {isHTMLDocument(message) ? (
              stripHtml(message)
            ) : (
              <Markdown useParagraphMargins>{message}</Markdown>
            )}
          </Text>
        </Box>
        {links && (
          <Row gap={2} flexWrap="wrap" flexShrink={0}>
            {links?.map((link, idx) => {
              const props = generateLinkProps(
                link,
                origin,
                sync?.destination,
                sync?.segment,
                sync?.segment?.connection,
                sync,
              );
              return props ? (
                <Row key={idx}>
                  <Link
                    href={props.url ?? "#"}
                    isExternal={link?.type === "external"}
                  >
                    <Button icon={props.icon} size="md" variant="secondary">
                      {props.label}
                    </Button>
                  </Link>
                </Row>
              ) : null;
            })}
          </Row>
        )}
      </Column>
    </Dialog>
  );
};

const isHTMLDocument = (message: string) => message.includes(`<!DOCTYPE html>`);

/**
 * Pull text out just in case we get HTML as an error response.
 */
function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
}
